import {
  useImageListState,
  useToast,
} from '~/common/components/defaultUIComponents';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import JobPictureApi from '~/common/api/zeiterfassungServerApi/JobPictureApi';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import * as durationUtils from '~/common/utils/durationUtils';
import { Job } from '~/common/entities/Project/ProjectTypes';
import { ID } from '~/common/api/common/apiCommons';
import * as ReactRouter from 'react-router-dom';
import { FormikHelpers } from 'formik';
import React from 'react';
import _ from 'lodash';

export const useCreateEditJobForm = (
  jobID?: ID,
  projectID?: ID,
  enabled = true,
) => {
  const navigate = ReactRouter.useNavigate();
  const toast = useToast({});

  const {
    initialImages,
    setInitialImages,
    setImages,
    addedImages,
    removedImages,
  } = useImageListState();
  const [{ data: job }] = JobApi.hooks.useGetJobQuery(
    { id: jobID },
    {
      enabled: !!jobID && enabled,
    },
  );

  JobPictureApi.hooks.usePicturesOfJobQuery(job, {
    enabled: !!job,
    onSuccess: (pictures) => {
      const pics = pictures?.map((pic) => ({
        id: pic.id,
        dataURL: `/images/job/${pic.id}/`,
      }));
      setInitialImages(pics ?? []);
    },
  });
  const { mutateAsync: removeJobPictures } =
    JobPictureApi.hooks.useRemoveJobPicturesMutation(job);

  const { mutateAsync: createJobPictures } =
    JobPictureApi.hooks.useCreateJobPicturesMutation();

  const [{ data: projectJobs }] = JobApi.hooks.useJobsOfProjectQuery(
    { id: projectID },
    { enabled: !!projectID },
  );

  const { mutateAsync: rotatePicture } =
    JobPictureApi.hooks.useRotatePicture(job);

  const { mutateAsync: createJob } = JobApi.hooks.useCreateJobMutation();
  const { mutateAsync: editJob } = JobApi.hooks.useEditJobMutation();

  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery();

  const initialValues = React.useMemo(() => {
    const next_step_number =
      (_.max(_.map(projectJobs, (pj) => pj.step_number)) ?? 0) + 1;
    return {
      name: job?.name ?? '',
      description: job?.description ?? '',
      step_number: job?.step_number ?? next_step_number,
      done_per_project: job?.done_per_project ?? 1,
      important_message: job?.important_message ?? '',
      department: job?.department?.id,
      ...durationUtils.asHMS(
        durationUtils.secondsToDuration(job?.duration as number),
      ),
    };
  }, [job, projectJobs]);

  const submit = React.useCallback(
    (
      { hours, minutes, seconds, ...values }: typeof initialValues,
      helpers: FormikHelpers<typeof initialValues>,
    ) => {
      let req: Promise<Job | null>;
      if (!job && projectID) {
        req = createJob({
          ...values,
          project: projectID as string,
          duration: durationUtils.normalizeToSeconds({
            hours,
            minutes,
            seconds,
          }),
        });
      } else if (job && job.projects) {
        req = editJob({
          id: job.id,
          ...values,
          projects: job.projects.map((p) => p.id),
          duration: durationUtils.normalizeToSeconds({
            hours,
            minutes,
            seconds,
          }),
        }).then((response) => response?.job ?? null);
      } else {
        return;
      }

      return req
        .then((job) => removeJobPictures(removedImages).then(() => job))
        .then((job) => job && createJobPictures({ job, images: addedImages }))
        .then(() => helpers.resetForm())
        .then(() => navigate(-1))
        .catch(() =>
          toast({
            status: 'error',
            title: !job ? 'createJob.error' : 'editJob.error',
          }),
        );
    },
    [job, projectID, addedImages, removedImages],
  );

  return {
    initialValues,
    submit,
    job,
    departments,
    setImages,
    initialImages,
    projectJobs,
    rotatePicture,
  };
};
